import { Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ButtonDigited } from "../Button/Button.variants";
import { CommonModal } from "../Commons/Modal.component";

export type ResourceDescriptionModalProps = {
  actionDisabled?: boolean
  actionIcon: ReactNode
  actionLabel: string
  description: string
  icon: ReactNode
  id: string
  onAction: () => void
  onClose: () => void
  open: boolean
  title: string
}

export function ResourceDescriptionModal({
  actionDisabled = false,
  actionIcon,
  actionLabel,
  description,
  icon,
  id,
  onAction,
  onClose,
  open,
  title
}: ResourceDescriptionModalProps) {
  const { t } = useTranslation();

  return (
    <CommonModal
      open={ open }
      handleClose={ onClose }
      avoidMobileSetting
      sxModalContent={ {
        borderRadius: "0.75rem",
        maxWidth: "calc(100% - 2rem)",
        overflowX: "hidden",
        padding: "1.5rem",
        width: "31.25rem"
      } }
    >
      <Stack id={ id } onClick={ (event) => event.stopPropagation() }>
        <Stack direction={ "row" } alignItems={ "center" } gap={ "0.5rem" } marginBottom={ "0.75rem" }>
          { icon }
          <ModalTitle component="h3">{ title }</ModalTitle>
          <CloseButton
            type="button"
            aria-label={ t("close") }
            onClick={ onClose }
          >
            <X size={ "1.25rem" } aria-hidden="true" />
          </CloseButton>
        </Stack>
        <ModalDescription>{ description }</ModalDescription>
        <Stack direction={ "row" } justifyContent={ "flex-end" } marginTop={ "0.5rem" }>
          <ButtonDigited
            typology="secondary"
            buttonSize="sm"
            disabled={ actionDisabled }
            onClick={ onAction }
            aria-label={ `${actionLabel} ${title}` }
          >
            <Stack direction={ "row" } alignItems={ "center" } gap={ "0.5rem" } component="span">
              { actionLabel }
              { actionIcon }
            </Stack>
          </ButtonDigited>
        </Stack>
      </Stack>
    </CommonModal>
  );
}

const ModalTitle = styled(Typography)<{ component?: React.ElementType }>(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  flex: 1,
  fontSize: "1rem",
  fontWeight: 600,
  lineHeight: "1.5rem",
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
}));

const ModalDescription = styled(Typography)(({ theme }) => ({
  color: theme.customColors.exploreCardText,
  fontSize: "0.875rem",
  lineHeight: "1.25rem",
  
  overflowWrap: "anywhere",
  whiteSpace: "pre-line",
  wordBreak: "break-word"
}));

const CloseButton = styled("button")(({ theme }) => ({
  alignItems: "center",
  background: "none",
  border: "none",
  color: theme.customColors.exploreCardText,
  cursor: "pointer",
  display: "flex",
  flexShrink: 0,
  padding: 0
}));
